import { Link, useParams } from "react-router-dom";
import { formatDate, getArticleById, getMovieById, getRelatedArticles } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";

export default function NewsDetail() {
  const { id } = useParams();
  const article = getArticleById(id);
  const movie = article ? getMovieById(article.movieId) : undefined;

  usePageMeta(
    article ? `${article.title} - 80s电影影视资讯` : "文章不存在 - 80s电影",
    article
      ? article.excerpt
      : "该文章可能已被移除或链接地址有误，可返回影评资讯列表继续阅读。",
    article
      ? `${article.type},80年代电影,${movie ? `${movie.title},` : ""}电影影评,影视资讯`
      : "80年代电影影评,影视资讯"
  );

  if (!article) {
    return (
      <div className="mx-auto max-w-page px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          icon="ri-article-line"
          title="文章不存在或已下架"
          description="我们没有找到这篇文章。它可能已经被替换，或者链接地址有误。"
          action={
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
            >
              返回影评资讯列表
              <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          }
        />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影评资讯", to: "/news" },
              { label: article.type },
            ]}
          />
          <span className="mt-5 inline-block rounded-full bg-primary-100 px-3 py-1 font-label text-[11px] tracking-[0.16em] text-primary-800">
            {article.type}
          </span>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-secondary-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读约 {article.readTime}
            </span>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-secondary-200">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} - 80s电影`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <p className="mt-3 text-[11px] text-secondary-500">配图为站内原创插画，用于文章氛围示意。</p>

            <div className="mt-7 space-y-5">
              {article.content.map((paragraph, index) => (
                <p
                  key={index}
                  className={`text-sm leading-[1.95] text-foreground-700 md:text-base ${
                    index === 0 ? "font-medium text-foreground-800" : ""
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 self-start rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                返回影评资讯列表
              </Link>
              <p className="text-[11px] text-secondary-500">
                本文观点仅代表作者个人，欢迎在阅读后形成自己的判断。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              {movie && (
                <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
                  <p className="font-label text-[11px] uppercase tracking-[0.2em] text-secondary-600">
                    文中提到的影片
                  </p>
                  <Link to={`/movie/${movie.id}`} className="group mt-3 flex gap-3">
                    <div className="h-28 w-20 shrink-0 overflow-hidden rounded-md bg-secondary-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 海报`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-700">
                        {movie.title}
                      </p>
                      <p className="mt-1 text-[11px] text-secondary-600">
                        {movie.year} · {movie.genres.join(" / ")}
                      </p>
                      <p className="mt-1.5 line-clamp-2 text-xs text-foreground-600">{movie.tagline}</p>
                    </div>
                  </Link>
                  <Link
                    to={`/movie/${movie.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700"
                  >
                    查看完整影片资料
                    <i className="ri-arrow-right-line text-[11px]"></i>
                  </Link>
                </div>
              )}

              <div className="rounded-lg border border-background-200 p-4">
                <p className="font-label text-[11px] uppercase tracking-[0.2em] text-secondary-600">
                  相关文章推荐
                </p>
                <ul className="mt-3 divide-y divide-background-200">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link to={`/news/${item.id}`} className="group block py-3">
                        <p className="text-[11px] text-secondary-500">
                          {item.type} · {formatDate(item.date)}
                        </p>
                        <p className="mt-1 text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}